import React from 'react'
import {get, last} from 'lodash'
import {useLocation} from 'wouter'
import {useWindowSize} from 'react-use'
import Select, {ActionMeta, SingleValue} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import {CSSTransition} from 'react-transition-group'
import toast, {Toaster, ToastBar} from 'react-hot-toast'
import * as zod from 'zod'
import {Controller as FormController} from 'react-hook-form'
import {useSuspense} from '@data-client/react'
import {SessionState, useAppNav, useToggle, useSession, FormFieldProps, val2opt, SelectOption, useForm} from '../lib'
import {Trip, UserScope, UserScopeResource} from '../lib/model'
import {UseFormReturn} from 'react-hook-form'
import {
  Alert,
  AlertColor,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  InputBaseComponentProps,
  Rating,
  TextField,
  Typography,
} from '@mui/material'
import {
  IconAbout,
  IconClose,
  IconConceal,
  IconDisabled,
  IconEnabled,
  IconError,
  IconHome,
  IconLoading,
  IconReveal,
  IconSubmit,
  IconSuccess,
  IconWarning,
} from '../Icons'
import {DateTimePicker} from '@mui/x-date-pickers'

export function Loading({msg = 'Loading'}) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      fontSize="large"
      width="100%"
      height="100%"
    >
      <CircularProgress />
      <Box mt={3}>{msg}</Box>
    </Box>
  )
}

export function Footer() {
  return (
    <Box id="footer" className="app-flex" bgcolor="primary.main" fontSize="small">
      <Box>
        <Button href="/" sx={{color: 'common.white', fontSize: 'small'}}>
          <IconHome /> &nbsp; Home
        </Button>
        <Button href="./about" sx={{color: 'common.white', fontSize: 'small'}}>
          <IconAbout /> &nbsp; About
        </Button>
      </Box>
      <Box>
        <Box component="small" color="secondary.main">
          powered by
        </Box>
        &nbsp;
        <Button href="http://tfks.net" sx={{color: 'common.white'}}>
          TFKS
        </Button>
      </Box>
    </Box>
  )
}

export function Authenticating() {
  return <Loading msg="Authenticating" />
}

export interface AppMsgProps {
  children: React.ReactNode
  severity?: AlertColor
  size?: 'small' | 'medium' | 'large'
}

export function AppMsg({children, severity = 'info', size = 'large'}: AppMsgProps) {
  const sx = {p: 3, my: 5, fontSize: '2rem', textAlign: 'center', fontVariant: 'all-small-caps'}
  if (size === 'small') {
    sx.p = 1
    sx.my = 2
    sx.fontSize = '1.4rem'
  } else if (size === 'medium') {
    sx.p = 2
    sx.my = 3
    sx.fontSize = '1.6rem'
  }
  return (
    <Alert severity={severity} variant="filled" icon={false} sx={sx}>
      {children}
    </Alert>
  )
}

export function AppToaster() {
  return (
    <Toaster containerStyle={{top: 5, right: 5, position: 'fixed'}}>
      {t => {
        let MyIcon = IconLoading
        let severity: AlertColor = 'info'
        if (t.type === 'success') {
          MyIcon = IconSuccess
          severity = 'success'
        } else if (t.type === 'custom') {
          MyIcon = IconWarning
          severity = 'warning'
        } else if (t.type === 'error') {
          MyIcon = IconError
          severity = 'error'
        }
        return (
          <ToastBar toast={t}>
            {({message}) => (
              <Alert severity={severity} icon={false}>
                <MyIcon />
                <div>{message}</div>
                <IconButton onClick={() => toast.dismiss(t.id)} sx={{ml: 'auto'}}>
                  <IconClose />
                </IconButton>
              </Alert>
            )}
          </ToastBar>
        )
      }}
    </Toaster>
  )
}

// A simple way to add collapsability.  Assumes layout:
// <Collapse>
//  <element-to-click/>
//  <elements-to-toggle/>...
// </Collapse>
//
type CollapseProps = {
  once?: boolean
  children: React.ReactElement[]
}
export const Collapse: React.FC<CollapseProps> = ({once = false, children}) => {
  const nodeRef = React.useRef<HTMLDivElement>(null)
  const show = useToggle()
  const clicker = React.cloneElement(children[0], {
    onClick: (e: React.SyntheticEvent) => {
      show.toggle()
      if (children[0].props.onClick) {
        children[0].props.onClick(e)
      }
    },
    style: {...children[0].props.style, cursor: 'pointer'},
  })
  return (
    <>
      {(!show.enabled || !once) && clicker}
      <CSSTransition nodeRef={nodeRef} in={show.enabled} timeout={300} classNames="collapse" unmountOnExit>
        <div ref={nodeRef}>{show && children.slice(1)}</div>
      </CSSTransition>
    </>
  )
}

export function UserNoCanDo() {
  return (
    <Container maxWidth="sm">
      <AppMsg severity="error">
        This View Is For
        <br />
        Team Leaders Only
      </AppMsg>
    </Container>
  )
}

export function SectionHeader({children}: {children: React.ReactNode}) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
      {children}
    </Box>
  )
}

export function RequiredFieldNote() {
  return <div className="required-note">Required: *</div>
}

export function RequiredFieldIndicator() {
  return <span className="text-danger fw-bold ms-2">*</span>
}

export type PanelProps = {
  session: SessionState
  params: Record<string, string>
  trips?: Trip[] // TODO: wean off this hack
}

export type PanelOptions = {
  CLOSE_BEHAVIOR?: string
}

// TODO: figure out how to use enum:
// - isolatedModules compiler option?
// - https://ncjamieson.com/dont-export-const-enums/
// export const CloseBehavior = {
//   home: 1,
// } as const
// type CloseBehavior = keyof typeof CloseBehavior

type DetailPanelProps = {
  panel: React.JSXElementConstructor<PanelProps> & PanelOptions
  props?: Record<string, unknown>
  path: string
  title: string[] | ((params: Record<string, string>) => string[])
  overlay?: boolean
  home?: (params: Record<string, string>) => string
}
export function DetailPanel({panel: Panel, props = {}, path, title, overlay = true, home}: DetailPanelProps) {
  const nav = useAppNav()
  const session = useSession()
  const settings = session.getSettings()
  const nodeToAnimate = React.useRef<HTMLDivElement>(null)
  const [location] = useLocation()
  const match = location.match(new RegExp(path, 'u'))
  let params: Record<string, string> = {}
  if (match) {
    params = match.groups || {}
  }
  let subtitle = []
  if (typeof title === 'function') {
    subtitle = title(params)
  } else {
    subtitle = title
  }
  if (match) {
    document.title = [settings.name]
      .concat(subtitle)
      .filter(i => i)
      .join(' : ')
  }

  const onClose = () => {
    if (Panel.CLOSE_BEHAVIOR !== 'home' && window.history.length > 2) {
      nav.back()
    } else if (home) {
      nav.to(home(params), {replace: true})
    } else if (params['home']) {
      nav.to(params['home'], {replace: true})
    } else {
      nav.to('/')
    }
  }

  // TODO: AddTrip does not transition out
  return (
    <CSSTransition nodeRef={nodeToAnimate} in={match != null} timeout={1000} classNames="detail-panel" unmountOnExit>
      <div ref={nodeToAnimate} id="detail-panel">
        {overlay && <div className="overlay" onClick={nav.back} />}
        <div className="contents">
          <Box className="header" color="primary.main" bgcolor="secondary.main">
            <div>{last(subtitle)}</div>
            <IconClose className="app-clickable" onClick={onClose} fontSize="large" />
          </Box>
          <div className="body">
            <React.Suspense fallback={<Loading />}>
              {match ? <Panel session={session} params={params} {...props} /> : null}
            </React.Suspense>
          </div>
        </div>
      </div>
    </CSSTransition>
  )
}

interface SimpleFormProps {
  form: UseFormReturn
  fields: FormFieldProps[]
}

export function SimpleForm({form, fields}: SimpleFormProps) {
  return (
    <Grid container columns={2} spacing={4}>
      {fields.map(field => (
        <Grid key={field.name} size={{xs: 2, md: 1}}>
          <Field form={form} attrs={field} />
        </Grid>
      ))}
    </Grid>
  )
}

function help(text: React.ReactNode, errmsg: string | undefined) {
  // const help = errmsg ? errmsg.message?.toString() : attrs.help
  if (!errmsg) {
    return text
  }
  return (
    <>
      <Typography paragraph={false} fontSize="0.75rem">
        {errmsg.toUpperCase()}
      </Typography>
      {text && (
        <Typography component="div" pt="0.5rem" sx={{color: 'text.secondary'}}>
          {text}
        </Typography>
      )}
    </>
  )
}

type DefaultControlProps = {
  form: UseFormReturn
  attrs: FormFieldProps
}

function DefaultControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  let type = attrs.type || 'text'
  let multiline = false
  if (type === 'textarea') {
    type = 'text'
    multiline = true
  }
  return (
    <TextField
      {...form.register(name)}
      error={Boolean(errmsg)}
      helperText={help(attrs.help, errmsg?.message?.toString())}
      type={attrs.type || 'text'}
      multiline={multiline}
      placeholder={attrs.placeholder || ''}
      autoComplete={attrs.type === 'password' ? 'current-password' : ''}
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      rows={attrs.rows}
      fullWidth={true}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
    />
  )
}

function ShowablePasswordControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const reveal = useToggle()
  const PasswordIcon = reveal.enabled ? IconReveal : IconConceal
  const errmsg = get(form.formState.errors, name)
  return (
    <TextField
      {...form.register(name)}
      error={Boolean(errmsg)}
      helperText={help(attrs.help?.toString(), errmsg?.message?.toString())}
      type={reveal.enabled ? 'text' : 'password'}
      placeholder="****************************"
      autoComplete="new-password"
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      fullWidth={true}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <PasswordIcon onClick={reveal.toggle} />
          </InputAdornment>
        ),
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
    />
  )
}

function ColorPickerControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  return (
    <TextField
      {...form.register(name)}
      error={Boolean(errmsg)}
      helperText={help(attrs.help?.toString(), errmsg?.message?.toString())}
      type="color"
      placeholder={attrs.placeholder || ''}
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      fullWidth={true}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
    />
  )
}

function DateControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  return (
    <FormController
      control={form.control}
      name={name}
      render={({field}) => {
        return (
          <DateTimePicker
            {...field}
            label={attrs.label + (attrs.required ? ' *' : '')}
            format="yyyy-MM-dd hh:mm:ss aa"
            slotProps={{
              textField: {
                error: Boolean(errmsg),
                helperText: help(attrs.help?.toString(), errmsg?.message?.toString()),
                fullWidth: true,
                FormHelperTextProps: {component: 'div', sx: {fontSize: '1rem'}},
              },
            }}
            closeOnSelect={true}
          />
        )
      }}
    />
  )
}

export function SwitchControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  const enabled = form.watch(name)
  const children = (
    <ButtonGroup size="small">
      <Button
        onClick={() => form.setValue(name, true, {shouldValidate: true})}
        color="success"
        variant={enabled === true ? 'contained' : 'outlined'}
      >
        {enabled === true && <IconEnabled sx={{mr: 1}} />}
        Enable{enabled === true && 'd'}
      </Button>
      <Button
        onClick={() => form.setValue(name, false, {shouldValidate: true})}
        color="error"
        variant={enabled === false ? 'contained' : 'outlined'}
      >
        {enabled === false && <IconDisabled sx={{mr: 1}} />}
        Disable{enabled === false && 'd'}
      </Button>
    </ButtonGroup>
  )
  const InputComponent = React.forwardRef<HTMLDivElement>((props, ref) => {
    return <div ref={ref} {...props} />
  })
  return (
    <TextField
      error={Boolean(errmsg)}
      helperText={help(attrs.help, errmsg?.message?.toString())}
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      fullWidth={true}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
      InputLabelProps={{shrink: true}}
      InputProps={{
        inputComponent: InputComponent as React.FunctionComponent<InputBaseComponentProps>,
      }}
      inputProps={{children}}
      sx={{textAlign: 'center'}}
    />
  )
}

export function RatingControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  // TODO: replace with Slider
  const children = (
    <Rating
      name={name}
      defaultValue={form.getValues(name)}
      size="large"
      onChange={(e, value) => form.setValue(name, value)}
      sx={{display: 'flex', justifyContent: 'space-evenly'}}
    />
  )
  const InputComponent = React.forwardRef<HTMLDivElement>((props, ref) => {
    return <div ref={ref} {...props} />
  })
  return (
    <TextField
      error={Boolean(errmsg)}
      helperText={help(attrs.help, errmsg?.message?.toString())}
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      fullWidth={true}
      // see https://github.com/mui/material-ui/issues/33339
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
      InputLabelProps={{shrink: true}}
      InputProps={{
        inputComponent: InputComponent as React.FunctionComponent<InputBaseComponentProps>,
      }}
      inputProps={{children}}
    />
  )
}

export function SelectControl({form, attrs}: DefaultControlProps) {
  const name = attrs.name
  const errmsg = get(form.formState.errors, name)
  const MySelect = attrs.creatable ? CreatableSelect : Select
  // TODO: https://mui.com/material-ui/react-autocomplete/#free-solo
  const children = (
    <FormController
      control={form.control}
      name={name}
      render={({field}) => {
        return (
          <MySelect
            {...field}
            options={attrs.options}
            placeholder={attrs.placeholder}
            isMulti={attrs.multi}
            isSearchable={attrs.searchable}
            className={form.formState.errors[name] && 'is-invalid'}
            classNamePrefix="react-select"
          />
        )
      }}
    />
  )
  const InputComponent = React.forwardRef<HTMLDivElement>((props, ref) => {
    return <div {...props} ref={ref} style={{height: 'unset'}} />
  })
  return (
    <TextField
      error={Boolean(errmsg)}
      helperText={help(attrs.help, errmsg?.message?.toString())}
      variant="outlined"
      label={attrs.label + (attrs.required ? ' *' : '')}
      fullWidth={true}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FormHelperTextProps={{component: 'div', sx: {fontSize: '1rem'}} as any}
      InputLabelProps={{shrink: true}}
      InputProps={{
        inputComponent: InputComponent as React.FunctionComponent<InputBaseComponentProps>,
      }}
      inputProps={{children}}
    />
  )
}

type FieldProps = {
  form: UseFormReturn
  attrs: FormFieldProps
  children?: React.ReactNode
}

export function Field({form, attrs, children = null}: FieldProps) {
  const name = attrs.name
  // TODO: markdown
  const error = form.formState.errors[name]
  // NOTE: using useCallback to avoid input focus issues on transition to & from errors
  const control = React.useCallback(() => {
    if (attrs.type === 'showable-password') {
      return <ShowablePasswordControl form={form} attrs={attrs} />
    } else if (attrs.type === 'switch') {
      return <SwitchControl form={form} attrs={attrs} />
      // } else if (attrs.type === 'address') {
      //   return <AddressControl form={form} name={name} attrs={attrs} />
      // } else if (attrs.type === 'image') {
      //   return <ImageControl form={form} name={name} attrs={attrs as ImageControlAttrs} />
    } else if (attrs.type === 'date') {
      return <DateControl form={form} attrs={attrs} />
    } else if (attrs.type === 'color') {
      return <ColorPickerControl form={form} attrs={attrs} />
    } else if (attrs.type === 'rating') {
      return <RatingControl form={form} attrs={attrs} />
    } else if (attrs.type === 'select') {
      return <SelectControl form={form} attrs={attrs} />
    }
    return <DefaultControl form={form} attrs={attrs} />
  }, [error?.message]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!attrs) {
    return <div>FORM FIELD NOT DEFINED: ${name}</div>
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children || control()}</>
}

type SelectTagsProps = {
  form: UseFormReturn
  attrs: FormFieldProps
  tags: string[]
}
export function SelectTags({form, attrs, tags}: SelectTagsProps) {
  const options = tags.map(val2opt) as SelectOption[]
  return <SelectControl form={form} attrs={{...attrs, options}} />
}

export type SelectUserChangeHandler = (
  newValue: SingleValue<{value: UserScope; label: JSX.Element}>,
  actionMeta: ActionMeta<{value: UserScope; label: JSX.Element}>
) => void

const ManageUserSchema = zod.object({
  user: zod.object({
    value: zod.object({
      id: zod.number(),
    }),
    label: zod.object({}),
  }),
})
type ManageUserSchema = zod.infer<typeof ManageUserSchema>

export function SelectUser({session}: PanelProps) {
  const [location] = useLocation()
  const members = useSuspense(UserScopeResource.getList, {})
  const winsize = useWindowSize()
  const form = useForm(ManageUserSchema)
  // const disabled = Boolean(location.match(REGEX.lockedScope))
  const label = (i: UserScope) => (
    <Box display="flex" alignItems="center" key={`scope-${i.id}`}>
      <Avatar alt={i.name} src={i.avatar} sx={{mr: 2, width: 50, height: 50, boxShadow: '4px 2px 6px #999'}} />
      <div className="app-truncate">
        {i.name} <br /> {i.email}
      </div>
    </Box>
  )
  const showValue = useToggle()
  const options = members.map(i => ({value: i, label: label(i)}))

  const onChange: SelectUserChangeHandler = item => {
    if (item) {
      // TODO: tryna work around dup key warning
      // item.label = React.cloneElement(item.label, {key: 'scope-selected'})
      form.setValue('user', item)
      showValue.on()
    }
  }
  /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
  /* @ts-expect-error */
  const onSubmit = form.handleSubmit((data: ManageUserSchema) => {
    const user = data.user.value
    if (user && user.id !== session.scope.id) {
      // reload page to help avoid cache issues - coupled to routes so make sure stays in-sync
      window.location.assign(location.replace(/[/]manage-user/, '') + `?scope=${user.id}`)
    }
  })

  return (
    <form onSubmit={onSubmit}>
      <FormController
        control={form.control}
        name="user"
        render={({field}) => {
          return (
            <Select
              {...field}
              onChange={onChange}
              options={options}
              menuIsOpen
              captureMenuScroll={false}
              menuShouldScrollIntoView={false}
              maxMenuHeight={winsize.height - 250}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              filterOption={(option, text) => {
                if (!option.value.search) {
                  return false
                }
                const m = text.toLowerCase().match(/^(id|email|name):(.+)/u)
                if (m) {
                  return m[2] === String(get(option.data.value, m[1]))
                }
                return option.data.value.search.indexOf(text.toLowerCase()) > -1
              }}
              controlShouldRenderValue={showValue.enabled}
              onFocus={showValue.off}
              autoFocus
              blurInputOnSelect
              // onMenuOpen={showValue.off}
              // onMenuClose={showValue.on}
              placeholder="Type to match user or choose from list..."
              classNamePrefix="react-select"
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  height: 75,
                  width: '100%',
                }),
                menu: provided => ({
                  ...provided,
                  position: 'relative',
                }),
              }}
            />
          )
        }}
      />
      <Box mt={3} textAlign="center">
        <SubmitButton form={form}>Confirm</SubmitButton>
      </Box>
    </form>
  )
}

// TODO: support custom icon
interface SubmitButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  form: UseFormReturn
  children: React.ReactNode
}

export function SubmitButton({form, children}: SubmitButtonProps) {
  return (
    <Button
      type="submit"
      loading={form.formState.isSubmitting}
      loadingPosition="start"
      startIcon={<IconSubmit />}
      variant="contained"
    >
      {children}
    </Button>
  )
}

interface ActionHeaderProps {
  title: string
  children?: React.ReactNode
}
export function ActionHeader({title, children}: ActionHeaderProps) {
  return (
    <Box className="app-flex" flexWrap="wrap" my={1}>
      <Typography variant="h2">{title}</Typography>
      <Box className="app-flex">{children}</Box>
    </Box>
  )
}
